import React, { useState } from 'react';
import Api from '@app/api/Api.js';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  job_title: '',
};

function validateField(name, fields) {
  switch (name) {
    case 'email': {
      if (!fields.email.trim()) return Drupal.tt('Campo obbligatorio');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) return Drupal.tt("L'indirizzo mail non sembra valido");
      return '';
    }
    case 'phone': {
      if (!fields.phone.trim()) return Drupal.tt('Campo obbligatorio');
      if (!/^\+[0-9]{6,}$/.test(fields.phone.trim())) return Drupal.tt('Formato non valido. Usa il prefisso internazionale (es. +393401234567)');
      return '';
    }
    default:
      return !fields[name]?.trim() ? Drupal.tt('Campo obbligatorio') : '';
  }
}

function validate(fields) {
  return Object.keys(fields).reduce((errors, name) => {
    const err = validateField(name, fields);
    if (err) errors[name] = err;
    return errors;
  }, {});
}

const FIELD_NAMES = Object.keys(EMPTY_FORM);

export default function RegistrationForm({ onSuccess }) {
  console.log(Drupal.url('api/registration'));
  const [fields, setFields] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const allTouched = FIELD_NAMES.every(name => touched[name]);
  const hasErrors = Object.values(errors).some(Boolean);
  const isDisabled = submitting || !allTouched || hasErrors;

  function set(name, value) {
    setFields(prev => {
      const next = { ...prev, [name]: value };
      if (touched[name]) setErrors(e => ({ ...e, [name]: validateField(name, next) }));
      return next;
    });
  }

  function handleBlur(name) {
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, fields) }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const validation = validate(fields);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setSubmitting(true);
    setServerError('');
    try {
      await Api.post('api/registration', fields);
      onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message ?? Drupal.tt('Si è verificato un errore. Riprova più tardi.');
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="registration-form" onSubmit={handleSubmit} noValidate>
      <div className="row mt-5">
        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Nome')}</label>
          <div className={`registration-form__input-wrapper${errors.first_name ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="text"
              value={fields.first_name}
              onChange={e => set('first_name', e.target.value)}
              onBlur={() => handleBlur('first_name')}
              disabled={submitting}
            />
            {fields.first_name && (
              <button type="button" className="registration-form__clear" onClick={() => set('first_name', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.first_name && <span className="registration-form__error">{errors.first_name}</span>}
        </div>

        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Cognome')}</label>
          <div className={`registration-form__input-wrapper${errors.last_name ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="text"
              value={fields.last_name}
              onChange={e => set('last_name', e.target.value)}
              onBlur={() => handleBlur('last_name')}
              disabled={submitting}
            />
            {fields.last_name && (
              <button type="button" className="registration-form__clear" onClick={() => set('last_name', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.last_name && <span className="registration-form__error">{errors.last_name}</span>}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Email')}</label>
          <div className={`registration-form__input-wrapper${errors.email ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="email"
              value={fields.email}
              onChange={e => set('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              disabled={submitting}
            />
            {fields.email && (
              <button type="button" className="registration-form__clear" onClick={() => set('email', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.email && <span className="registration-form__error">{errors.email}</span>}
        </div>

        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Numero di telefono')}</label>
          <div className={`registration-form__input-wrapper${errors.phone ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="tel"
              value={fields.phone}
              onChange={e => set('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              disabled={submitting}
            />
            {fields.phone && (
              <button type="button" className="registration-form__clear" onClick={() => set('phone', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.phone && <span className="registration-form__error">{errors.phone}</span>}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Azienda / Ragione sociale')}</label>
          <div className={`registration-form__input-wrapper${errors.company ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="text"
              value={fields.company}
              onChange={e => set('company', e.target.value)}
              onBlur={() => handleBlur('company')}
              disabled={submitting}
            />
            {fields.company && (
              <button type="button" className="registration-form__clear" onClick={() => set('company', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.company && <span className="registration-form__error">{errors.company}</span>}
        </div>

        <div className="col-md-6 mb-4">
          <label>*{Drupal.tt('Ruolo')}</label>
          <div className={`registration-form__input-wrapper${errors.job_title ? ' registration-form__input-wrapper--error' : ''}`}>
            <input
              type="text"
              value={fields.job_title}
              onChange={e => set('job_title', e.target.value)}
              onBlur={() => handleBlur('job_title')}
              disabled={submitting}
            />
            {fields.job_title && (
              <button type="button" className="registration-form__clear" onClick={() => set('job_title', '')} tabIndex={-1}>✕</button>
            )}
          </div>
          {errors.job_title && <span className="registration-form__error">{errors.job_title}</span>}
        </div>
      </div>

      {serverError && <div className="alert alert-danger mb-4">{serverError}</div>}

      <div className="d-flex justify-content-between mt-4">
        <button type="button" className="btn btn-secondary" onClick={() => onSuccess(true)} disabled={submitting}>
          {Drupal.tt('annulla')}
        </button>
        <button type="submit" className="btn-primary" disabled={isDisabled}>
          {submitting ? '...' : Drupal.tt('conferma')}
        </button>
      </div>
    </form>
  );
}
